import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import MainLayout from '@/features/common/components/MainLayout';

interface StreamJob {
  id?: string;
  title?: string | null;
  companyName?: string | null;
  jobSource?: string;
  logoUrl?: string | null;
}

const CUBLI_LOGO = '/cibli-logo.svg';

const SOURCE_COLORS: Record<string, string> = {
  Cibli: '#21c294',
  CibliLegacy: '#009f7f',
  FranceTravail: '#bad6fc',
};

/** Priority 1: Clearbit HD logo */
function clearbitUrl(domain: string): string {
  return `https://logo.clearbit.com/${domain}`;
}

const fallbackJobs: StreamJob[] = [
  {
    title: 'Vendeur en magasin',
    companyName: 'Leclerc',
    jobSource: 'Cibli',
    logoUrl: clearbitUrl('leclerc.com'),
  },
  {
    title: 'Développeur Web Full Stack',
    companyName: 'Cibli',
    jobSource: 'Cibli',
    logoUrl: CUBLI_LOGO,
  },
  {
    title: 'Aide-soignant(e)',
    companyName: 'CHU Lille',
    jobSource: 'FranceTravail',
    logoUrl: clearbitUrl('chru-lille.fr'),
  },
  {
    title: 'Conducteur de bus',
    companyName: 'RATP',
    jobSource: 'FranceTravail',
    logoUrl: clearbitUrl('ratp.fr'),
  },
  {
    title: 'Agent de sécurité',
    companyName: 'Securitas',
    jobSource: 'Cibli',
    logoUrl: clearbitUrl('securitas.com'),
  },
  {
    title: 'Livreur',
    companyName: 'Amazon',
    jobSource: 'Cibli',
    logoUrl: clearbitUrl('amazon.fr'),
  },
  {
    title: 'Cuisinier(ère)',
    companyName: 'Elior',
    jobSource: 'Cibli',
    logoUrl: clearbitUrl('elior-group.com'),
  },
  {
    title: 'Cariste',
    companyName: 'Decathlon',
    jobSource: 'FranceTravail',
    logoUrl: clearbitUrl('decathlon.com'),
  },
  {
    title: 'Développeur React/TypeScript',
    companyName: 'Cibli',
    jobSource: 'Cibli',
    logoUrl: CUBLI_LOGO,
  },
  {
    title: 'Chef de rang',
    companyName: 'Accor',
    jobSource: 'FranceTravail',
    logoUrl: clearbitUrl('accor.com'),
  },
  {
    title: 'Commercial(e)',
    companyName: 'Orange',
    jobSource: 'Cibli',
    logoUrl: clearbitUrl('orange.com'),
  },
  {
    title: 'Infirmier(ère)',
    companyName: 'AP-HP',
    jobSource: 'FranceTravail',
    logoUrl: clearbitUrl('aphp.fr'),
  },
  {
    title: 'Manutentionnaire',
    companyName: 'Amazon',
    jobSource: 'Cibli',
    logoUrl: clearbitUrl('amazon.fr'),
  },
  {
    title: 'Technicien(ne) informatique',
    companyName: 'Cibli',
    jobSource: 'Cibli',
    logoUrl: CUBLI_LOGO,
  },
  {
    title: 'Réceptionniste',
    companyName: 'Accor',
    jobSource: 'FranceTravail',
    logoUrl: clearbitUrl('accor.com'),
  },
];

/** Preload all fallback logos into browser cache so they appear instantly */
function preloadLogos(jobs: StreamJob[]) {
  const urls = new Set<string>();
  for (const job of jobs) {
    if (job.logoUrl) urls.add(job.logoUrl);
  }
  for (const url of urls) {
    const img = new Image();
    img.src = url;
  }
}

interface Props {
  message?: string;
  streamedJobs?: StreamJob[];
}

export default function LoadingScreen({ message, streamedJobs = [] }: Props) {
  const { t } = useTranslation();
  const trackRef = useRef<HTMLDivElement>(null);
  const offsetRef = useRef(0);
  const [elapsed, setElapsed] = useState(0);

  // Preload fallback logos on mount so they're cached when carousel renders
  useEffect(() => {
    preloadLogos(fallbackJobs);
  }, []);

  // Elapsed timer: increment every second to show slow message after 4s
  useEffect(() => {
    const interval = setInterval(() => setElapsed(e => e + 1), 1_000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    let rafId: number;
    const speed = 0.5;

    const tick = () => {
      offsetRef.current -= speed;
      const trackWidth = track.scrollWidth / 2;
      if (Math.abs(offsetRef.current) >= trackWidth) offsetRef.current = 0;
      track.style.transform = `translateX(${offsetRef.current}px)`;
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, []);

  const displayText = message || t('MatchingJobs.loading');
  const displayJobs = streamedJobs.length > 0 ? streamedJobs : fallbackJobs;
  const doubled = [...displayJobs, ...displayJobs];

  return (
    <MainLayout>
      <div className="flex flex-1 flex-col items-center justify-center gap-6 overflow-hidden w-full max-w-full">
        <img src="/insigne-vert-noir.svg" alt="CibliJob" className="h-20 w-auto" />

        <p className="text-lg text-gray-600 text-center max-w-sm">{displayText}</p>

        {elapsed >= 4 && (
          <p className="text-sm text-gray-400 text-center max-w-sm animate-pulse">
            {t('MatchingJobs.loading_slow')}
          </p>
        )}

        <div
          className="w-full overflow-hidden px-4"
          style={{
            maskImage: 'linear-gradient(to right, transparent, black 5%, black 95%, transparent)',
          }}
        >
          <div ref={trackRef} className="flex gap-3 sm:gap-4 justify-center">
            {doubled.map((job, i) => {
              const source = job.jobSource ?? 'Cibli';
              const color = SOURCE_COLORS[source] ?? '#21c294';
              const initial = (job.companyName ?? '?')[0]?.toUpperCase() ?? '?';
              const title = job.title ?? t('MatchingJobs.loading');

              return (
                <div
                  key={`${job.id ?? 'fb'}-${i}`}
                  className="flex items-center gap-2 sm:gap-3 bg-white rounded-xl px-3 sm:px-4 py-2.5 sm:py-3 shadow-sm border border-gray-100 shrink-0"
                >
                  {job.logoUrl ? (
                    <img
                      src={job.logoUrl}
                      alt={job.companyName ?? ''}
                      className="w-8 h-8 rounded-lg object-cover shrink-0"
                      onError={e => {
                        const img = e.target as HTMLImageElement;
                        img.style.display = 'none';
                        const fallback = img.nextElementSibling as HTMLElement;
                        if (fallback) fallback.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div
                    className="w-8 h-8 rounded-lg items-center justify-center text-white text-xs font-bold shrink-0"
                    style={{
                      background: color,
                      display: job.logoUrl ? 'none' : 'flex',
                    }}
                  >
                    {initial}
                  </div>
                  <div className="whitespace-nowrap">
                    <p className="text-sm font-semibold text-[#254441]">{title}</p>
                    <p className="text-xs text-gray-400">{job.companyName ?? source}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
